import { Row } from '@tanstack/react-table';
import {
  Button,
  ButtonLink,
  Container,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  RiDeleteBinLine,
  RiMore2Line,
  RiPencilFill,
  Spinner,
  Stack,
} from 'component-library';
import { TextInput } from 'component-library/TextInput/TextInput';
import i18next from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PlotGroupComparison } from '@/api/rest/resources/types/organization';
import { SuspenseBoundary } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { SortButton } from '@/components/widgets/o/GenericTable/SortButton';
import { useDebounce } from '@/hooks/useDebounce';
import { usePathSet } from '@/hooks/usePathSet';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { isNullish } from '@/utils/booleans';
import { MetadataColumn } from '@/utils/table/mergeColumns';

import { ComingSoon } from '../components/ComingSoon';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { useOrganizationPlotGroupComparisons } from '../hooks/useOrganizationPlotGroupComparisons';
import { DeleteComparisonWithConfirmation } from './components/DeleteComparisonWithConfirmation';
import { EditComparison } from './components/EditPlotGroupComparison';

const ActionsCell = ({
  row,
  confirmComparisonEdit,
  confirmComparisonDelete,
}: {
  row: Row<PlotGroupComparison>;
  confirmComparisonEdit: (group: PlotGroupComparison) => void;
  confirmComparisonDelete: (group: PlotGroupComparison) => void;
}) => {
  const comparison = row.original;
  const { t } = useTranslation();

  return (
    <Dropdown modal={true}>
      <DropdownTrigger data-testid='comparison-row-overflow-menu'>
        <div className='w-10 shrink-0 rounded-full border border-primary-100 p-2 text-primary-100 hover:bg-neutral-black-4'>
          <RiMore2Line size={24} />
        </div>
      </DropdownTrigger>

      <DropdownContent side='bottom' sideOffset={10}>
        <DropdownItem onClick={() => confirmComparisonEdit(comparison)} leftAdornment={<RiPencilFill />}>
          {t('global.compare.editComparison.title')}
        </DropdownItem>
        <DropdownItem onClick={() => confirmComparisonDelete(comparison)} leftAdornment={<RiDeleteBinLine />}>
          {t('global.compare.deleteComparison.title')}
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};

const ComparisonsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathSet = usePathSet();

  const [comparisonForEdit, setComparisonForEdit] = useState<PlotGroupComparison | null>(null);
  const [comparisonForDeletion, setComparisonForDeletion] = useState<PlotGroupComparison | null>(null);

  const organizationId = useOrganizationId();
  const comparisons = useOrganizationPlotGroupComparisons(organizationId).data;

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideActions = isLimitedAccessUser || !isOrganizationMember;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const filteredComparisons = useMemo(() => {
    if (isNullish(debouncedSearchQuery.trim())) return comparisons;

    const queryString = debouncedSearchQuery.toLowerCase();

    return comparisons.filter((comparison) => comparison.name?.toLowerCase().includes(queryString));
  }, [comparisons, debouncedSearchQuery]);

  const confirmComparisonDelete = (comparison: PlotGroupComparison) => {
    setComparisonForDeletion(comparison);
  };

  const confirmComparisonEdit = (comparison: PlotGroupComparison) => {
    setComparisonForEdit(comparison);
  };

  const defaultColumns = useMemo<MetadataColumn<PlotGroupComparison>[]>(() => {
    return [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.compare.table.headers.name.title`)}
          </SortButton>
        ),
        cell: ({ getValue }) => (
          <span className='flex items-center gap-2'>
            <span className='bg-neutral-hover w-12 h-12 rounded-md items-center flex justify-center'>
              <Icon icon='ri-layout-column-line' className='w-4 p-0 m-0 text-primary-100' />
            </span>
            {getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'modified_at',
        header: ({ column }) => (
          <SortButton column={column} className='-ml-1.5 px-0'>
            {t(`global.compare.table.headers.modified_at.title`)}
          </SortButton>
        ),
        cell: ({ getValue }) => <span>{new Date(getValue() as string).toLocaleDateString(i18next.language)}</span>,
      },
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (hideActions) {
            return null;
          }

          return (
            <span className='self-end'>
              <ActionsCell
                row={row}
                confirmComparisonDelete={confirmComparisonDelete}
                confirmComparisonEdit={confirmComparisonEdit}
              />
            </span>
          );
        },
      },
    ];
  }, [t, hideActions]);

  const onRowClick = (row: Row<PlotGroupComparison>) => {
    const comparison = row.original;
    navigate(`${pathSet.compare}?comparisonId=${comparison.id}`, {
      state: {
        previousPath: pathSet.comparisons,
      },
    });
  };

  const tooltipContent = {
    name: t('global.compare.table.headers.name.tooltip'),
    modified_at: t('global.compare.table.headers.modified_at.tooltip'),
  };
  if (comparisons.length === 0) {
    return (
      <Stack center className='justify-center items-center gap-6 h-1/4 p-16'>
        <span className='typography-h4'>{t('global.compare.noComparisons')}</span>
        <ButtonLink type='button' variant='outline' to={pathSet.compare}>
          {t('global.compare.actions.newComparison')}
        </ButtonLink>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={8} data-testid='plot-group-comparisons-list' className='w-full overflow-hidden scrollbar-hidden'>
        <Stack className='flex flex-row items-center justify-between gap-2'>
          <span className='typography-overline'>
            {t('global.compare.table.pagination.comparisons', {
              count: filteredComparisons.length,
            })}
          </span>
          <Stack className='flex h-11 flex-row gap-1 bg-neutral-black-4 border border-neutral-black-4 rounded-full justify-start px-3 py-1 items-center min-w-52'>
            <Icon icon='ri-search-line' className='text-md' />
            <TextInput
              placeholder={t('shared.searchBar.placeholder')}
              className='w-full typography-body1 border-none bg-transparent h-full focus:border-none focus:outline-none focus-visible:outline-none px-0 py-0'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button
                variant='text'
                className='text-text-secondary hover:text-text-primary px-1 py-1'
                onClick={() => setSearchQuery('')}
              >
                <Icon icon='ri-close-line' className='text-sm' />
              </Button>
            )}
          </Stack>
        </Stack>
        <GenericTable
          data={filteredComparisons}
          defaultColumns={defaultColumns}
          onRowClick={onRowClick}
          tooltipContent={tooltipContent}
        />
      </Stack>
      <DeleteComparisonWithConfirmation
        comparison={comparisonForDeletion}
        onClose={() => setComparisonForDeletion(null)}
      />
      {!isNullish(comparisonForEdit) && (
        <EditComparison
          organizationId={organizationId}
          plotGroupComparison={comparisonForEdit}
          open={!isNullish(comparisonForEdit)}
          onOpenChange={(open) => setComparisonForEdit(open ? comparisonForEdit : null)}
        />
      )}
    </>
  );
};

export const CompareListPage = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideComparisons = isLimitedAccessUser || !isOrganizationMember;

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      navBarProps={{
        title: t('navbar.comparisons'),
        showFilters: false,
        children: !hideComparisons ? (
          <Stack direction='row' className='justify-end gap-4'>
            <ButtonLink type='button' variant='outline' to={pathSet.compare}>
              {t('global.compare.actions.newComparison')}
            </ButtonLink>
          </Stack>
        ) : null,
      }}
    >
      <Container gutterWidth={{ sm: 2 }} className='gap-10 my-8 ' data-testid='compare-list-page'>
        {hideComparisons ? (
          <ComingSoon />
        ) : (
          <SuspenseBoundary
            fallback={
              <Stack center className='absolute h-full w-full bg-neutral-white-50'>
                <Spinner size={10} />
              </Stack>
            }
          >
            <ComparisonsTable />
          </SuspenseBoundary>
        )}
      </Container>
    </SignedInPageLayout>
  );
};
